'use client';
/* eslint-disable react/style-prop-object --
   The DS ui/Button exposes a variant prop literally named `style`
   (style="filled" | "stroke" | ...), which collides with this lint rule's
   assumption that any `style` prop is the DOM style object. The usage is correct. */

import React, { useState } from 'react';
import { toast } from 'react-toastify';

// Legacy components (all migrated to DS tokens)
import {
  Button,
  Card,
  CardStats,
  Status,
  FeedbackStatus,
  FeedbackLoadingSpinner,
  FeedbackLoadingOverlay,
  InputSearch,
  StepIndicator,
  Pagination,
  SearchableDropdown,
  UniversalFilter,
  SideBarModal,
  ActionDropdown,
} from '@/components';

// DS primitive components (src/components/ui/)
import { Button as DSButton } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Label } from '@/components/ui/label';
import { InputRoot, Input, InputIcon, InputAffix } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

// DS DatePicker primitives (react-day-picker + radix popover)
import { DatePicker, DateRangePicker } from '@/components/ui/date-picker';
import type { DateRange } from 'react-day-picker';

// Legacy hand-rolled DatePicker (kept for before/after comparison)
import LegacyDatePicker from '@/components/DatePicker/DatePicker';

// Icons
import {
  RiSearchLine,
  RiMailLine,
  RiUserLine,
} from '@remixicon/react';

// ─────────────────────────────────────────────────────────
// Section layout helpers
// ─────────────────────────────────────────────────────────

interface SectionProps {
  id: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ id, title, description, children }) => (
  <section id={id} className="scroll-mt-16 space-y-4">
    <div>
      <h2 className="text-xl font-semibold text-text-strong-950">{title}</h2>
      {description && (
        <p className="text-sm text-text-sub-600 mt-1 max-w-2xl">{description}</p>
      )}
    </div>
    <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 p-6 space-y-8">
      {children}
    </div>
  </section>
);

const Sub: React.FC<{ label: string }> = ({ label }) => (
  <p className="text-[11px] font-semibold uppercase tracking-widest text-text-soft-400 mb-3">
    {label}
  </p>
);

const Row: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => (
  <div className={`flex flex-wrap items-center gap-3 ${className}`}>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────
// Static data
// ─────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  { id: 'colors',     label: 'Colors' },
  { id: 'buttons',    label: 'Buttons' },
  { id: 'spinners',   label: 'Spinners' },
  { id: 'form',       label: 'Form' },
  { id: 'status',     label: 'Status' },
  { id: 'cards',      label: 'Cards' },
  { id: 'steps',      label: 'Steps' },
  { id: 'pagination', label: 'Pagination' },
  { id: 'datepicker', label: 'Date Pickers' },
  { id: 'dropdown',   label: 'Dropdown' },
  { id: 'filter',     label: 'Filter' },
  { id: 'modal',      label: 'Modal' },
  { id: 'loading',    label: 'Loading' },
  { id: 'toast',      label: 'Toast' },
];

const DROPDOWN_OPTIONS = [
  { value: 'option1', label: 'Option One' },
  { value: 'option2', label: 'Option Two' },
  { value: 'option3', label: 'Option Three' },
  { value: 'option4', label: 'Option Four' },
  { value: 'option5', label: 'Option Five' },
];

const STEPS_DATA = [
  { id: 1, label: 'Details' },
  { id: 2, label: 'Review' },
  { id: 3, label: 'Confirm' },
];

const FILTER_CONFIG = [
  {
    key: 'status',
    title: 'Status',
    icon: 'ri-checkbox-circle-line',
    multiple: true,
    staticOptions: [
      { key: 'active',   label: 'Active',   value: 'active' },
      { key: 'pending',  label: 'Pending',  value: 'pending' },
      { key: 'closed',   label: 'Closed',   value: 'closed' },
    ],
  },
  {
    key: 'type',
    title: 'Type',
    icon: 'ri-tag-line',
    multiple: false,
    staticOptions: [
      { key: 'rent', label: 'Rent',       value: 'rent' },
      { key: 'rto',  label: 'Rent to Own', value: 'rto' },
    ],
  },
];

// ─────────────────────────────────────────────────────────
// Showcase page
// ─────────────────────────────────────────────────────────

const ShowcasePage: React.FC = () => {
  // Form
  const [inputVal, setInputVal]   = useState('');
  const [searchVal, setSearchVal] = useState('');
  const [dsDate, setDsDate]       = useState<Date | undefined>(undefined);
  const [dsRange, setDsRange]     = useState<DateRange | undefined>(undefined);
  const [legacyDate, setLegacyDate] = useState('');

  // Pagination
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(10);

  // Steps
  const [activeStep, setActiveStep] = useState(2);

  // Dropdowns
  const [singleVal, setSingleVal]     = useState<string>('');
  const [multiVal, setMultiVal]       = useState<(string | number)[]>([]);

  // Modals
  const [centerModalOpen, setCenterModalOpen] = useState(false);
  const [sideModalOpen,   setSideModalOpen]   = useState(false);

  // Loading overlay
  const [overlayVisible, setOverlayVisible] = useState(false);

  // Action dropdown
  const [actionOpen, setActionOpen] = useState(false);

  // Filter
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});

  const showOverlay = () => {
    setOverlayVisible(true);
    setTimeout(() => setOverlayVisible(false), 2000);
  };

  return (
    <div className="p-6 space-y-10 max-w-5xl">
      {/* ── Page header ── */}
      <div className="space-y-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-text-strong-950">
            Design System Showcase
          </h1>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-feature-lighter text-primary border border-feature-light">
            Dash DS · Layer 0–1
          </span>
        </div>
        <p className="text-sm text-text-sub-600 max-w-2xl">
          Every component below has been migrated from raw Tailwind palette
          classes to semantic DS tokens — no hard-coded hex, no{' '}
          <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">gray-*</code> or{' '}
          <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">purple-*</code> anywhere outside{' '}
          <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">src/components/ui/</code>.
        </p>
      </div>

      {/* ── Sticky section nav ── */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-bg-white-0 border-b border-stroke-soft-200">
        <div className="flex items-center gap-1 overflow-x-auto">
          {NAV_SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md text-text-sub-600 hover:bg-bg-weak-50 hover:text-text-strong-950 transition-colors"
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════ */}
      {/* 1. Colors                                  */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="colors"
        title="Color Tokens"
        description="Semantic DS tokens replace all raw Tailwind palette classes. Layer 0 brand tokens are locked — never hard-code."
      >
        <div>
          <Sub label="Brand / Primary (Dash Purple #5e2aac)" />
          <div className="flex flex-wrap gap-5">
            {[
              { bg: 'bg-primary',                  border: '',                              label: 'primary\n(base)' },
              { bg: 'bg-(--primary-light)',         border: 'border border-stroke-soft-200', label: 'primary\nlight' },
              { bg: 'bg-(--primary-dark)',          border: '',                              label: 'primary\ndark' },
              { bg: 'bg-feature-lighter',           border: 'border border-stroke-soft-200', label: 'feature\nlighter' },
              { bg: 'bg-feature-light',             border: 'border border-stroke-soft-200', label: 'feature\nlight' },
              { bg: 'bg-feature-base',              border: '',                              label: 'feature\nbase' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-lg ${s.bg} ${s.border}`} />
                <span className="text-[10px] text-text-sub-600 text-center whitespace-pre leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Sub label="State Tokens" />
          <div className="flex flex-wrap gap-5">
            {[
              { bg: 'bg-success-base',          label: 'success\nbase' },
              { bg: 'bg-success-lighter',       label: 'success\nlighter', border: true },
              { bg: 'bg-error-base',            label: 'error\nbase' },
              { bg: 'bg-error-lighter',         label: 'error\nlighter',   border: true },
              { bg: 'bg-away-base',             label: 'away\nbase' },
              { bg: 'bg-away-lighter',          label: 'away\nlighter',    border: true },
              { bg: 'bg-warning-base',          label: 'warning\nbase' },
              { bg: 'bg-warning-lighter',       label: 'warning\nlighter', border: true },
              { bg: 'bg-information-base',      label: 'info\nbase' },
              { bg: 'bg-information-lighter',   label: 'info\nlighter',    border: true },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-lg ${s.bg} ${s.border ? 'border border-stroke-soft-200' : ''}`} />
                <span className="text-[10px] text-text-sub-600 text-center whitespace-pre leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Sub label="Surface / Background" />
          <div className="flex flex-wrap gap-5">
            {[
              { bg: 'bg-bg-white-0',    border: true,  label: 'bg-white-0' },
              { bg: 'bg-bg-weak-50',    border: true,  label: 'bg-weak-50' },
              { bg: 'bg-bg-soft-200',   border: false, label: 'bg-soft-200' },
              { bg: 'bg-bg-sub-300',    border: false, label: 'bg-sub-300' },
              { bg: 'bg-bg-strong-950', border: false, label: 'bg-strong\n950' },
            ].map((s) => (
              <div key={s.label} className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-lg ${s.bg} ${s.border ? 'border border-stroke-soft-200' : ''}`} />
                <span className="text-[10px] text-text-sub-600 text-center whitespace-pre leading-tight">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 2. Buttons                                 */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="buttons"
        title="Buttons"
        description="Legacy Button (used across pages) + DS ui/Button primitive (tone × style × size)."
      >
        <div>
          <Sub label="Legacy Button — variants" />
          <Row>
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="neutral">Neutral</Button>
            <Button variant="white">White</Button>
            <Button variant="primary" disabled>Disabled</Button>
          </Row>
        </div>

        <div>
          <Sub label="DS ui/Button — tone × style matrix" />
          <div className="space-y-4">
            {(['primary', 'neutral', 'destructive'] as const).map((tone) => (
              <div key={tone}>
                <p className="text-xs capitalize text-text-sub-600 mb-2">{tone}</p>
                <Row>
                  {(['filled', 'stroke', 'lighter', 'ghost', 'link'] as const).map((style) => (
                    <DSButton key={style} tone={tone} style={style} size="sm">
                      {style}
                    </DSButton>
                  ))}
                </Row>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Sub label="DS ui/Button — sizes (tone: primary, style: filled)" />
          <Row className="items-end">
            {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((size) => (
              <DSButton key={size} tone="primary" style="filled" size={size}>
                {size.toUpperCase()}
              </DSButton>
            ))}
          </Row>
        </div>

        <div>
          <Sub label="DS ui/Button — loading state" />
          <Row>
            <DSButton tone="primary" style="filled" loading>Saving…</DSButton>
            <DSButton tone="neutral" style="stroke" loading>Fetching…</DSButton>
            <DSButton tone="destructive" style="filled" loading>Deleting…</DSButton>
          </Row>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 3. Spinners                                */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="spinners"
        title="Spinner"
        description="DS Spinner replaces the hand-rolled animate-spin div in App.tsx and FeedbackLoading."
      >
        <div>
          <Sub label="Sizes — tone: primary" />
          <Row className="items-end">
            {(['xs', 'sm', 'md', 'lg', 'xl'] as const).map((s) => (
              <div key={s} className="flex flex-col items-center gap-2">
                <Spinner size={s} tone="primary" />
                <span className="text-[10px] text-text-soft-400">{s}</span>
              </div>
            ))}
          </Row>
        </div>

        <div>
          <Sub label="Tones — size: lg" />
          <div className="flex flex-wrap gap-6">
            {(['primary', 'neutral', 'destructive'] as const).map((t) => (
              <div key={t} className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-lg bg-bg-weak-50 flex items-center justify-center">
                  <Spinner size="lg" tone={t} />
                </div>
                <span className="text-[10px] text-text-sub-600">{t}</span>
              </div>
            ))}
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-lg bg-bg-strong-950 flex items-center justify-center">
                <Spinner size="lg" tone="white" />
              </div>
              <span className="text-[10px] text-text-sub-600">white</span>
            </div>
          </div>
        </div>

        <div>
          <Sub label="FeedbackLoadingSpinner — legacy wrapper (maps to DS Spinner)" />
          <Row className="items-end">
            {(['small', 'medium', 'large'] as const).map((s) => (
              <div key={s} className="flex flex-col items-center gap-2">
                <FeedbackLoadingSpinner size={s} />
                <span className="text-[10px] text-text-soft-400">{s}</span>
              </div>
            ))}
          </Row>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 4. Form Elements                           */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="form"
        title="Form Elements"
        description="DS Label, InputRoot/Input, PasswordInput, InputSearch — tokens: bg-bg-white-0, border-stroke-soft-200, focus ring-primary."
      >
        <div>
          <Sub label="DS Label — required / optional / with hint" />
          <div className="flex flex-wrap gap-6">
            <Label htmlFor="l1" required>Required field</Label>
            <Label htmlFor="l2" optional>Optional field</Label>
            <Label htmlFor="l3" hint="Must be at least 8 characters">With hint</Label>
          </div>
        </div>

        <div>
          <Sub label="DS InputRoot + Input — sizes" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
            {(['sm', 'md', 'lg', 'xl'] as const).map((size) => (
              <div key={size} className="space-y-1">
                <Label htmlFor={`inp-${size}`}>{size.toUpperCase()} input</Label>
                <InputRoot size={size}>
                  <Input
                    id={`inp-${size}`}
                    placeholder={`Placeholder (${size})`}
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                  />
                </InputRoot>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Sub label="DS Input — with icons & affixes" />
          <div className="flex flex-col gap-3 max-w-sm">
            <InputRoot size="md">
              <InputIcon><RiSearchLine className="size-4" /></InputIcon>
              <Input placeholder="Search something…" />
            </InputRoot>

            <InputRoot size="md">
              <InputIcon><RiMailLine className="size-4" /></InputIcon>
              <Input placeholder="user" type="text" />
              <InputAffix>@dash.co</InputAffix>
            </InputRoot>

            <InputRoot size="md">
              <InputIcon><RiUserLine className="size-4" /></InputIcon>
              <Input placeholder="Username" />
            </InputRoot>

            {/* error state */}
            <InputRoot size="md" data-invalid="true">
              <Input placeholder="Error state (data-invalid)" />
            </InputRoot>
          </div>
        </div>

        <div>
          <Sub label="DS PasswordInput" />
          <div className="max-w-sm">
            <PasswordInput placeholder="Enter password" />
          </div>
        </div>

        <div>
          <Sub label="InputSearch — legacy (border-stroke-sub-300, focus:ring-primary)" />
          <div className="max-w-sm">
            <InputSearch
              value={searchVal}
              onChange={setSearchVal}
              placeholder="Search records…"
            />
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 5. Status & Feedback Badges                */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="status"
        title="Status Badges"
        description="Status (domain-aware key→config map) + FeedbackStatus (explicit variant prop). All colors via DS state tokens."
      >
        <div>
          <Sub label="Status component — feedback type" />
          <Row>
            {['active', 'completed', 'pending', 'failed', 'cancelled', 'in progress', 'online', 'offline'].map((v) => (
              <Status key={v} value={v} type="feedback" />
            ))}
          </Row>
        </div>

        <div>
          <Sub label="Status component — contract type" />
          <Row>
            <Status value="RTO" type="contract" />
            <Status value="RENT" type="contract" />
          </Row>
        </div>

        <div>
          <Sub label="FeedbackStatus — all 6 variants" />
          <Row>
            <FeedbackStatus status="Completed"  variant="success"  />
            <FeedbackStatus status="In Progress" variant="warning" />
            <FeedbackStatus status="Failed"      variant="error"   />
            <FeedbackStatus status="Reviewing"   variant="info"    />
            <FeedbackStatus status="Cancelled"   variant="neutral" />
            <FeedbackStatus status="Pending"     variant="pending" />
          </Row>
        </div>

        <div>
          <Sub label="FeedbackStatus — sizes" />
          <Row className="items-center">
            <FeedbackStatus status="Small"  variant="success" size="small"  />
            <FeedbackStatus status="Medium" variant="success" size="medium" />
            <FeedbackStatus status="Large"  variant="success" size="large"  />
          </Row>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 6. Cards                                   */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="cards"
        title="Cards"
        description="Card (content container) + CardStats (metric tile). bg-bg-white-0, border-stroke-soft-200."
      >
        <div>
          <Sub label="Card — plain container" />
          <Card title="Sample card">
            <p className="text-sm text-text-sub-600">
              Uses{' '}
              <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">bg-bg-white-0</code> and{' '}
              <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">border-stroke-soft-200</code>.
              Replace this with real module content.
            </p>
          </Card>
        </div>

        <div>
          <Sub label="CardStats — 6 color variants" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <CardStats title="Total Fleet" value="1,240" icon="ri-car-line" color="default" />
            <CardStats title="Active Jobs" value="84" icon="ri-briefcase-line" color="blue" />
            <CardStats
              title="On-time Rate"
              value="99%"
              icon="ri-checkbox-circle-line"
              color="green"
              subtitle="+2.4% vs last month"
              trend={{ value: 2.4, direction: 'up' }}
            />
            <CardStats title="Pending" value="12" icon="ri-time-line" color="yellow" />
            <CardStats
              title="Incidents"
              value="3"
              icon="ri-error-warning-line"
              color="red"
              subtitle="-1 since yesterday"
              trend={{ value: 1, direction: 'down' }}
            />
            <CardStats title="Charging" value="47" icon="ri-flashlight-line" color="purple" />
          </div>
        </div>

        <div>
          <Sub label="CardStats — loading skeleton (bg-bg-soft-200 shimmer)" />
          <div className="grid grid-cols-3 gap-4">
            <CardStats title="" value="" loading />
            <CardStats title="" value="" loading />
            <CardStats title="" value="" loading />
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 7. Step Indicator                          */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="steps"
        title="Step Indicator"
        description="Completed → bg-success-base · Active → bg-primary · Upcoming → bg-bg-weak-50."
      >
        <div>
          <Sub label="Interactive" />
          <div className="space-y-4">
            <StepIndicator steps={STEPS_DATA} currentStep={activeStep} />
            <Row>
              <DSButton
                tone="neutral"
                style="stroke"
                size="sm"
                onClick={() => setActiveStep((p) => Math.max(1, p - 1))}
                disabled={activeStep <= 1}
              >
                ← Previous
              </DSButton>
              <DSButton
                tone="primary"
                style="filled"
                size="sm"
                onClick={() => setActiveStep((p) => Math.min(4, p + 1))}
                disabled={activeStep >= 4}
              >
                Next →
              </DSButton>
            </Row>
          </div>
        </div>

        <div>
          <Sub label="All states — static" />
          <div className="space-y-4">
            <div>
              <p className="text-xs text-text-soft-400 mb-2">Step 1 active (all upcoming)</p>
              <StepIndicator steps={STEPS_DATA} currentStep={1} />
            </div>
            <div>
              <p className="text-xs text-text-soft-400 mb-2">Step 2 active (step 1 complete)</p>
              <StepIndicator steps={STEPS_DATA} currentStep={2} />
            </div>
            <div>
              <p className="text-xs text-text-soft-400 mb-2">All complete (step 4+)</p>
              <StepIndicator steps={STEPS_DATA} currentStep={4} />
            </div>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 8. Pagination                              */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="pagination"
        title="Pagination"
        description="Active page: bg-primary / text-static-white. Borders: border-stroke-sub-300."
      >
        <Pagination
          currentPage={page}
          totalItems={243}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        />
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 9. Date Pickers                            */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="datepicker"
        title="Date Pickers"
        description="DS DatePicker — radix Popover + react-day-picker Calendar, Figma 1:1. Selected: bg-(--primary-base) · Today: bold purple · Hover: bg-bg-weak-50."
      >
        <div>
          <Sub label="DS DatePicker — single date (3 sizes)" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            {(['sm', 'md', 'lg'] as const).map((size) => (
              <div key={size} className="space-y-1.5">
                <Label>{size.toUpperCase()}</Label>
                <DatePicker
                  size={size}
                  value={dsDate}
                  onValueChange={setDsDate}
                  placeholder="Pick a date"
                />
              </div>
            ))}
          </div>
          {dsDate && (
            <p className="text-xs text-text-sub-600 mt-2">
              Selected: <span className="font-medium text-text-strong-950">{dsDate.toDateString()}</span>
            </p>
          )}
        </div>

        <div>
          <Sub label="DS DatePicker — disabled" />
          <div className="max-w-xs">
            <DatePicker value={undefined} disabled placeholder="Disabled picker" />
          </div>
        </div>

        <div>
          <Sub label="DS DateRangePicker — two-month range calendar" />
          <div className="max-w-sm">
            <DateRangePicker
              value={dsRange}
              onValueChange={setDsRange}
              placeholder="Pick a date range"
            />
          </div>
          {dsRange?.from && (
            <p className="text-xs text-text-sub-600 mt-2">
              Range:{' '}
              <span className="font-medium text-text-strong-950">
                {dsRange.from.toDateString()}
                {dsRange.to ? ` → ${dsRange.to.toDateString()}` : ''}
              </span>
            </p>
          )}
        </div>

        {/* Before / after comparison */}
        <div className="pt-4 border-t border-stroke-soft-200">
          <Sub label="↪ Legacy hand-rolled DatePicker (native input — DS tokens only, not the DS component)" />
          <div className="max-w-xs">
            <LegacyDatePicker
              value={legacyDate}
              onChange={setLegacyDate}
              placeholder="Old picker (native <input type=date>)"
            />
          </div>
          <p className="text-xs text-text-soft-400 mt-2">
            The DS picker above is a true popover calendar matching Figma; this legacy
            one only re-skinned the browser-native date input. Use the DS one going forward.
          </p>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 10. Searchable Dropdown                    */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="dropdown"
        title="Searchable Dropdown"
        description="Single + multi-select. Selected: bg-feature-lighter / text-primary. Focus ring: primary."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
          <div className="space-y-2">
            <Sub label="Single select" />
            <SearchableDropdown
              options={DROPDOWN_OPTIONS}
              value={singleVal}
              onChange={(v) => setSingleVal(v as string)}
              placeholder="Choose an option…"
              allowClear
            />
            {singleVal && (
              <p className="text-xs text-text-sub-600">
                Selected: <span className="font-medium text-text-strong-950">{singleVal}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Sub label="Multi-select" />
            <SearchableDropdown
              multiple
              options={DROPDOWN_OPTIONS}
              value={multiVal}
              onChange={(v) => setMultiVal(v)}
              placeholder="Select multiple…"
              allowClear
            />
            {multiVal.length > 0 && (
              <p className="text-xs text-text-sub-600">
                Selected: <span className="font-medium text-text-strong-950">{multiVal.join(', ')}</span>
              </p>
            )}
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 11. Universal Filter                       */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="filter"
        title="Universal Filter"
        description="Filter badge count: bg-primary / text-static-white. Popover uses DS surface tokens."
      >
        <div className="space-y-3">
          <UniversalFilter
            filters={FILTER_CONFIG}
            currentFilters={filterValues}
            onApply={setFilterValues}
            onClear={() => setFilterValues({})}
            buttonText="Filter records"
          />
          {Object.keys(filterValues).length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-sub-600">Active filters:</span>
              <code className="text-xs bg-bg-weak-50 px-2 py-0.5 rounded-md border border-stroke-soft-200 text-text-strong-950">
                {JSON.stringify(filterValues)}
              </code>
            </div>
          )}
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 12. Action Dropdown                        */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="actions"
        title="Action Dropdown"
        description="3-dot menu — hover: bg-bg-weak-50. Destructive item: text-error-base / hover:bg-error-lighter."
      >
        <div className="flex items-center gap-4">
          <p className="text-sm text-text-sub-600">Click the ⋯ button:</p>
          <ActionDropdown
            record={{ id: 1 }}
            isOpen={actionOpen}
            onToggle={() => setActionOpen((p) => !p)}
            actions={[
              {
                key: 'view',
                label: 'View details',
                icon: 'ri-eye-line',
                onClick: () => { toast.info('View clicked'); },
              },
              {
                key: 'edit',
                label: 'Edit',
                icon: 'ri-pencil-line',
                onClick: () => { toast.success('Edit clicked'); },
                separateAfter: true,
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: 'ri-delete-bin-line',
                onClick: () => { toast.error('Delete clicked'); },
                className: 'text-error-base hover:bg-error-lighter',
              },
            ]}
          />
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 13. Modal / Sidebar Drawer                 */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="modal"
        title="Modal / Sidebar Drawer"
        description="Backdrop: bg-bg-strong-950/50. Card: bg-bg-white-0 · shadow-card-lg. Exit confirm uses bg-primary."
      >
        <Row>
          <DSButton
            tone="primary"
            style="filled"
            size="sm"
            onClick={() => setCenterModalOpen(true)}
          >
            Open center modal
          </DSButton>
          <DSButton
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => setSideModalOpen(true)}
          >
            Open side drawer
          </DSButton>
        </Row>

        {/* Center modal */}
        <SideBarModal
          isOpen={centerModalOpen}
          onClose={() => setCenterModalOpen(false)}
          position="center"
          width="md"
          header={
            <h3 className="text-base font-semibold text-text-strong-950">
              Center Modal
            </h3>
          }
          footer={
            <div className="flex justify-end gap-2">
              <DSButton
                tone="neutral"
                style="stroke"
                size="sm"
                onClick={() => setCenterModalOpen(false)}
              >
                Cancel
              </DSButton>
              <DSButton
                tone="primary"
                style="filled"
                size="sm"
                onClick={() => setCenterModalOpen(false)}
              >
                Confirm
              </DSButton>
            </div>
          }
        >
          <p className="text-sm text-text-sub-600">
            Backdrop:{' '}
            <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">bg-bg-strong-950/50</code>.
            Card:{' '}
            <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">shadow-card-lg</code>.
            Exit confirm button uses{' '}
            <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">bg-primary</code> — no inline hex.
          </p>
        </SideBarModal>

        {/* Side drawer */}
        <SideBarModal
          isOpen={sideModalOpen}
          onClose={() => setSideModalOpen(false)}
          position="right"
          width="md"
          header={
            <h3 className="text-base font-semibold text-text-strong-950">
              Side Drawer
            </h3>
          }
          footer={
            <div className="flex justify-end">
              <DSButton
                tone="neutral"
                style="stroke"
                size="sm"
                onClick={() => setSideModalOpen(false)}
              >
                Close
              </DSButton>
            </div>
          }
        >
          <p className="text-sm text-text-sub-600">
            Slides in from the right. Same DS surface tokens as the center modal.
          </p>
        </SideBarModal>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 14. Loading                                */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="loading"
        title="Loading States"
        description="FeedbackLoadingOverlay (full-screen) + AuthLoadingScreen (App.tsx route guard)."
      >
        <div>
          <Sub label="FeedbackLoadingOverlay — full-screen backdrop (auto-closes in 2s)" />
          <DSButton
            tone="primary"
            style="filled"
            size="sm"
            onClick={showOverlay}
          >
            Show loading overlay
          </DSButton>
          <FeedbackLoadingOverlay
            isVisible={overlayVisible}
            message="Processing request…"
          />
        </div>

        <div>
          <Sub label="AuthLoadingScreen — full-page route guard (App.tsx)" />
          <div className="rounded-lg border border-stroke-soft-200 bg-bg-white-0 h-36 flex flex-col items-center justify-center gap-3">
            <Spinner size="xl" tone="primary" />
            <p className="text-sm text-text-sub-600">Loading…</p>
          </div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════ */}
      {/* 15. Toast                                  */}
      {/* ══════════════════════════════════════════ */}
      <Section
        id="toast"
        title="Toast Notifications"
        description="react-toastify colors overridden in index.css via --toastify-color-* mapped to DS state tokens."
      >
        <Row>
          <DSButton
            tone="primary"
            style="lighter"
            size="sm"
            onClick={() => toast.success('Operation completed successfully!')}
          >
            <i className="ri-checkbox-circle-line mr-1.5" />
            Success
          </DSButton>
          <DSButton
            tone="destructive"
            style="lighter"
            size="sm"
            onClick={() => toast.error('Something went wrong. Please try again.')}
          >
            <i className="ri-close-circle-line mr-1.5" />
            Error
          </DSButton>
          <DSButton
            tone="neutral"
            style="lighter"
            size="sm"
            onClick={() => toast.warning('Check your input before proceeding.')}
          >
            <i className="ri-error-warning-line mr-1.5" />
            Warning
          </DSButton>
          <DSButton
            tone="neutral"
            style="lighter"
            size="sm"
            onClick={() => toast.info('New dashboard update available.')}
          >
            <i className="ri-information-line mr-1.5" />
            Info
          </DSButton>
          <DSButton
            tone="neutral"
            style="ghost"
            size="sm"
            onClick={() => toast('Default notification')}
          >
            Default
          </DSButton>
        </Row>
        <p className="text-xs text-text-sub-600 mt-1">
          CSS variables:{' '}
          <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">--toastify-color-success → var(--state-success-base)</code>,{' '}
          <code className="text-xs bg-bg-weak-50 px-1 rounded-sm">--toastify-toast-bd-radius: 12px</code>, font: Plus Jakarta Sans.
        </p>
      </Section>
    </div>
  );
};

export default ShowcasePage;
